import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Plus, Clock, Users } from 'lucide-react';
import { motion } from 'framer-motion';

const CalendarView = ({ bookings = [], onSelectBooking, onCreateBooking }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [view, setView] = useState('month'); // 'month' | 'week'

    // Get calendar data
    const calendarData = useMemo(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();

        if (view === 'month') {
            const firstDay = new Date(year, month, 1);
            const lastDay = new Date(year, month + 1, 0);
            const daysInMonth = lastDay.getDate();
            const startingDay = firstDay.getDay();

            const days = [];
            // Previous month's trailing days
            const prevMonth = new Date(year, month, 0);
            const prevMonthDays = prevMonth.getDate();
            for (let i = startingDay - 1; i >= 0; i--) {
                days.push({
                    date: new Date(year, month - 1, prevMonthDays - i),
                    isCurrentMonth: false
                });
            }
            // Current month's days
            for (let i = 1; i <= daysInMonth; i++) {
                days.push({
                    date: new Date(year, month, i),
                    isCurrentMonth: true
                });
            }
            // Next month's leading days
            const remainingDays = 42 - days.length; // 6 rows * 7 days
            for (let i = 1; i <= remainingDays; i++) {
                days.push({
                    date: new Date(year, month + 1, i),
                    isCurrentMonth: false
                });
            }

            return days;
        } else {
            // Week view
            const startOfWeek = new Date(currentDate);
            startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());

            const days = [];
            for (let i = 0; i < 7; i++) {
                const date = new Date(startOfWeek);
                date.setDate(startOfWeek.getDate() + i);
                days.push({
                    date,
                    isCurrentMonth: date.getMonth() === month
                });
            }
            return days;
        }
    }, [currentDate, view]);

    // Group bookings by date
    const bookingsByDate = useMemo(() => {
        const grouped = {};
        bookings.forEach(booking => {
            const dateKey = new Date(booking.start_time).toDateString();
            if (!grouped[dateKey]) {
                grouped[dateKey] = [];
            }
            grouped[dateKey].push(booking);
        });
        // Sort bookings within each day
        Object.keys(grouped).forEach(key => {
            grouped[key].sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
        });
        return grouped;
    }, [bookings]);

    const isToday = (date) => {
        const today = new Date();
        return date.toDateString() === today.toDateString();
    };

    const formatTime = (dateString) => {
        return new Date(dateString).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const navigatePrevious = () => {
        if (view === 'month') {
            setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
        } else {
            const newDate = new Date(currentDate);
            newDate.setDate(currentDate.getDate() - 7);
            setCurrentDate(newDate);
        }
    };

    const navigateNext = () => {
        if (view === 'month') {
            setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
        } else {
            const newDate = new Date(currentDate);
            newDate.setDate(currentDate.getDate() + 7);
            setCurrentDate(newDate);
        }
    };

    const navigateToday = () => {
        setCurrentDate(new Date());
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'confirmed': return 'bg-emerald-500';
            case 'pending': return 'bg-amber-500';
            case 'cancelled': return 'bg-red-500';
            default: return 'bg-slate-500';
        }
    };

    return (
        <div className="dark:bg-slate-900/50 bg-white rounded-xl border dark:border-slate-800 border-slate-200 overflow-hidden">
            {/* Calendar Header */}
            <div className="p-4 border-b dark:border-slate-800 border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                        <button
                            onClick={navigatePrevious}
                            className="p-2 rounded-lg dark:hover:bg-slate-800 hover:bg-slate-100 transition"
                        >
                            <ChevronLeft size={18} className="dark:text-slate-400 text-slate-500" />
                        </button>
                        <button
                            onClick={navigateNext}
                            className="p-2 rounded-lg dark:hover:bg-slate-800 hover:bg-slate-100 transition"
                        >
                            <ChevronRight size={18} className="dark:text-slate-400 text-slate-500" />
                        </button>
                    </div>

                    <h2 className="text-lg font-semibold dark:text-white text-slate-900">
                        {view === 'month'
                            ? currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                            : `Week of ${calendarData[0]?.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                        }
                    </h2>

                    <button
                        onClick={navigateToday}
                        className="px-3 py-1.5 text-sm rounded-lg dark:bg-slate-800 bg-slate-100 dark:text-slate-300 text-slate-600 dark:hover:bg-slate-700 hover:bg-slate-200 transition"
                    >
                        Today
                    </button>
                </div>

                <div className="flex items-center gap-2">
                    {/* View Toggle */}
                    <div className="flex dark:bg-slate-800 bg-slate-100 p-1 rounded-lg">
                        <button
                            onClick={() => setView('month')}
                            className={`px-3 py-1.5 text-sm rounded-md transition ${
                                view === 'month'
                                    ? 'dark:bg-slate-600 bg-white dark:text-white text-slate-900 shadow'
                                    : 'dark:text-slate-400 text-slate-500 hover:text-slate-900 dark:hover:text-white'
                            }`}
                        >
                            Month
                        </button>
                        <button
                            onClick={() => setView('week')}
                            className={`px-3 py-1.5 text-sm rounded-md transition ${
                                view === 'week'
                                    ? 'dark:bg-slate-600 bg-white dark:text-white text-slate-900 shadow'
                                    : 'dark:text-slate-400 text-slate-500 hover:text-slate-900 dark:hover:text-white'
                            }`}
                        >
                            Week
                        </button>
                    </div>
                </div>
            </div>

            {/* Calendar Grid */}
            <div className="p-4">
                {/* Day Headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="p-2 text-center text-xs font-medium dark:text-slate-500 text-slate-400 uppercase tracking-wider">
                            {day}
                        </div>
                    ))}
                </div>

                {/* Calendar Days */}
                {view === 'month' ? (
                    <div className="grid grid-cols-7 gap-1">
                        {calendarData.map((dayData, index) => {
                            const dateKey = dayData.date.toDateString();
                            const dayBookings = bookingsByDate[dateKey] || [];
                            const displayBookings = dayBookings.slice(0, 3);
                            const remainingCount = dayBookings.length - 3;

                            return (
                                <div
                                    key={index}
                                    className={`min-h-[100px] p-2 border rounded-lg transition cursor-pointer ${
                                        dayData.isCurrentMonth
                                            ? 'dark:border-slate-800 border-slate-200 dark:hover:bg-slate-800/50 hover:bg-slate-50'
                                            : 'dark:border-slate-800/50 border-slate-100 dark:bg-slate-900/30 bg-slate-50/50'
                                    } ${
                                        isToday(dayData.date)
                                            ? 'dark:border-sky-500/50 border-sky-500/50 dark:bg-sky-500/5 bg-sky-50/50'
                                            : ''
                                    }`}
                                    onClick={() => onCreateBooking?.()}
                                >
                                    <div className="flex items-center justify-between mb-1">
                                        <span className={`text-sm font-medium ${
                                            !dayData.isCurrentMonth
                                                ? 'dark:text-slate-600 text-slate-400'
                                                : isToday(dayData.date)
                                                ? 'text-sky-500'
                                                : 'dark:text-white text-slate-900'
                                        }`}>
                                            {dayData.date.getDate()}
                                        </span>
                                        {isToday(dayData.date) && (
                                            <span className="w-2 h-2 rounded-full bg-sky-500" />
                                        )}
                                    </div>

                                    <div className="space-y-1">
                                        {displayBookings.map((booking, i) => (
                                            <motion.div
                                                key={booking.id}
                                                initial={{ opacity: 0, scale: 0.9 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onSelectBooking?.(booking);
                                                }}
                                                className="group relative"
                                            >
                                                <div
                                                    className="px-2 py-1 rounded text-xs truncate cursor-pointer transition hover:opacity-80"
                                                    style={{
                                                        backgroundColor: `${booking.event_type?.color || '#3B82F6'}20`,
                                                        color: booking.event_type?.color || '#3B82F6'
                                                    }}
                                                >
                                                    <span className="font-medium">{formatTime(booking.start_time)}</span>
                                                    <span className="ml-1 opacity-75">{booking.title?.split(' ').slice(0, 2).join(' ')}</span>
                                                </div>
                                            </motion.div>
                                        ))}
                                        {remainingCount > 0 && (
                                            <div className="px-2 py-0.5 text-xs dark:text-slate-400 text-slate-500">
                                                +{remainingCount} more
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    /* Week View */
                    <div className="grid grid-cols-7 gap-2">
                        {calendarData.map((dayData, index) => {
                            const dateKey = dayData.date.toDateString();
                            const dayBookings = bookingsByDate[dateKey] || [];

                            return (
                                <div
                                    key={index}
                                    className={`min-h-[400px] border rounded-lg overflow-hidden ${
                                        isToday(dayData.date)
                                            ? 'dark:border-sky-500/50 border-sky-500/50'
                                            : 'dark:border-slate-800 border-slate-200'
                                    }`}
                                >
                                    {/* Day Header */}
                                    <div className={`p-3 border-b dark:border-slate-800 border-slate-200 text-center ${
                                        isToday(dayData.date)
                                            ? 'dark:bg-sky-500/10 bg-sky-50'
                                            : 'dark:bg-slate-800/50 bg-slate-50'
                                    }`}>
                                        <p className="text-xs dark:text-slate-400 text-slate-500 uppercase">
                                            {dayData.date.toLocaleDateString('en-US', { weekday: 'short' })}
                                        </p>
                                        <p className={`text-lg font-bold ${
                                            isToday(dayData.date) ? 'text-sky-500' : 'dark:text-white text-slate-900'
                                        }`}>
                                            {dayData.date.getDate()}
                                        </p>
                                    </div>

                                    {/* Bookings */}
                                    <div className="p-2 space-y-2">
                                        {dayBookings.length === 0 ? (
                                            <div
                                                onClick={() => onCreateBooking?.()}
                                                className="h-full min-h-[100px] flex items-center justify-center cursor-pointer group"
                                            >
                                                <div className="p-3 rounded-full dark:bg-slate-800/50 bg-slate-100 opacity-0 group-hover:opacity-100 transition">
                                                    <Plus size={18} className="dark:text-slate-500 text-slate-400" />
                                                </div>
                                            </div>
                                        ) : (
                                            dayBookings.map(booking => (
                                                <motion.div
                                                    key={booking.id}
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    onClick={() => onSelectBooking?.(booking)}
                                                    className="p-3 rounded-lg cursor-pointer transition hover:opacity-80"
                                                    style={{
                                                        backgroundColor: `${booking.event_type?.color || '#3B82F6'}15`,
                                                        borderLeft: `3px solid ${booking.event_type?.color || '#3B82F6'}`
                                                    }}
                                                >
                                                    <div className="flex items-center gap-1 text-xs dark:text-slate-400 text-slate-500 mb-1">
                                                        <Clock size={12} />
                                                        <span>{formatTime(booking.start_time)}</span>
                                                        <span className={`ml-auto px-1.5 py-0.5 rounded text-[10px] font-medium capitalize ${getStatusColor(booking.status)} text-white`}>
                                                            {booking.status}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm font-medium dark:text-white text-slate-900 truncate">
                                                        {booking.title?.split(' with ')[0]}
                                                    </p>
                                                    {booking.attendees?.[0] && (
                                                        <div className="flex items-center gap-1 mt-1 text-xs dark:text-slate-400 text-slate-500">
                                                            <Users size={12} />
                                                            <span className="truncate">{booking.attendees[0].name}</span>
                                                        </div>
                                                    )}
                                                </motion.div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Legend */}
            <div className="px-4 py-3 border-t dark:border-slate-800 border-slate-200 flex items-center gap-4 text-xs">
                <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span className="dark:text-slate-400 text-slate-500">Confirmed</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                    <span className="dark:text-slate-400 text-slate-500">Pending</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    <span className="dark:text-slate-400 text-slate-500">Cancelled</span>
                </div>
            </div>
        </div>
    );
};

export default CalendarView;
